<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBranding } from 'shared/composables/useBranding';
import { frontendURL } from 'dashboard/helper/URLHelper';
import IntegrationItem from './IntegrationItem.vue';
import McpServersIntegrationItem from './McpServersIntegrationItem.vue';
import McpEndpointIntegrationItem from './McpEndpointIntegrationItem.vue';
import CalendarsIntegrationItem from './CalendarsIntegrationItem.vue';
import TeamleaderIntegrationItem from './TeamleaderIntegrationItem.vue';
import HubspotIntegrationItem from './HubspotIntegrationItem.vue';
import MetaConversionsIntegrationItem from './MetaConversionsIntegrationItem.vue';
import GoogleAdsIntegrationItem from './GoogleAdsIntegrationItem.vue';
import OAuthApplicationsIntegrationItem from './OAuthApplicationsIntegrationItem.vue';
import ProviderCatalogItem from './shared/ProviderCatalogItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const DRIVE_PROVIDERS = [
  { id: 'google', icon: 'i-woot-google-drive' },
  { id: 'onedrive', icon: 'i-woot-sharepoint' },
  { id: 'sharepoint', icon: 'i-woot-sharepoint' },
  { id: 'dropbox', icon: 'i-woot-dropbox' },
];

const RESTAURANT_PROVIDERS = [
  {
    id: 'tablefever',
    i18nKey: 'TABLEFEVER',
    logo: 'tablefever.png',
  },
  {
    id: 'sevenrooms',
    i18nKey: 'SEVENROOMS',
    logo: 'sevenrooms.png',
  },
];

const CRM_ERP_APP_IDS = ['odoo'];

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const uiFlags = getters['integrations/getUIFlags'];
const accountId = getters.getCurrentAccountId;
const getDriveByProvider = getters['drives/getDriveByProvider'];
const getIntegration = getters['integrations/getIntegration'];

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const generalIntegrationList = computed(() =>
  integrationList.value.filter(item => !CRM_ERP_APP_IDS.includes(item.id))
);

const crmErpIntegrationList = computed(() =>
  integrationList.value.filter(item => CRM_ERP_APP_IDS.includes(item.id))
);

const driveCatalogItems = computed(() =>
  DRIVE_PROVIDERS.map(provider => {
    const drive = getDriveByProvider.value(provider.id);
    return {
      ...provider,
      name: t(
        `INTEGRATION_SETTINGS.DRIVES.PROVIDERS.${provider.id.toUpperCase()}`
      ),
      description: t(
        `INTEGRATION_SETTINGS.DRIVES.PROVIDER_DESCRIPTIONS.${provider.id.toUpperCase()}`
      ),
      enabled: !!drive,
      statusText: drive
        ? drive.name ||
          t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.CONNECTED_STATUS')
        : t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.NOT_CONNECTED'),
      to: frontendURL(
        `accounts/${accountId.value}/settings/integrations/drives/${provider.id}`
      ),
    };
  })
);

const restaurantCatalogItems = computed(() =>
  RESTAURANT_PROVIDERS.map(provider => {
    const hooks = getIntegration.value(provider.id)?.hooks || [];
    const connected = hooks.length > 0;
    return {
      ...provider,
      name: t(
        `INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDERS.${provider.i18nKey}.NAME`
      ),
      description: t(
        `INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDERS.${provider.i18nKey}.DESCRIPTION`
      ),
      enabled: connected,
      statusText: connected
        ? t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.CONNECTED_COUNT', {
            count: hooks.length,
          })
        : t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDER_EMPTY'),
      logoSrc: `/dashboard/images/integrations/${provider.logo}`,
      to: frontendURL(
        `accounts/${accountId.value}/settings/integrations/restaurant_widgets/${provider.id}`
      ),
    };
  })
);

onMounted(() => {
  store.dispatch('integrations/get');
  store.dispatch('mcpServers/get');
  store.dispatch('oauthApplications/get');
  store.dispatch('drives/get');
  store.dispatch('calendars/get');
  store.dispatch('teamleaders/get');
  store.dispatch('hubspots/get');
  store.dispatch('metaConversions/get');
  store.dispatch('googleAds/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <div class="flex flex-col gap-8">
          <div class="flex flex-col gap-4">
            <McpEndpointIntegrationItem />
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <McpServersIntegrationItem />
              <OAuthApplicationsIntegrationItem />
              <CalendarsIntegrationItem />

              <IntegrationItem
                v-for="item in generalIntegrationList"
                :id="item.id"
                :key="item.id"
                :logo="item.logo"
                :name="item.name"
                :description="item.description"
                :enabled="item.enabled"
              />
              <IntegrationItem
                id="dashboard_apps"
                :name="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.TITLE')"
                :description="
                  $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DESCRIPTION')
                "
                enabled="'true'"
              />
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.SECTIONS.DRIVES') }}
            </h3>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <ProviderCatalogItem
                v-for="item in driveCatalogItems"
                :key="item.id"
                :name="item.name"
                :description="item.description"
                :status-text="item.statusText"
                :enabled="item.enabled"
                :to="item.to"
                :icon="item.icon"
              />
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.SECTIONS.RESTAURANT_WIDGETS') }}
            </h3>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <ProviderCatalogItem
                v-for="item in restaurantCatalogItems"
                :key="item.id"
                :name="item.name"
                :description="item.description"
                :status-text="item.statusText"
                :enabled="item.enabled"
                :to="item.to"
                :logo-src="item.logoSrc"
              />
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.SECTIONS.ADS') }}
            </h3>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <MetaConversionsIntegrationItem />
              <GoogleAdsIntegrationItem />
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.SECTIONS.CRM_ERP') }}
            </h3>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <TeamleaderIntegrationItem />
              <HubspotIntegrationItem />
              <IntegrationItem
                v-for="item in crmErpIntegrationList"
                :id="item.id"
                :key="item.id"
                :logo="item.logo"
                :name="item.name"
                :description="item.description"
                :enabled="item.enabled"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
